'use client';

/**
 * Canonical POSITION card — the mockup-#01/#02 surface rendered on market
 * detail (and, via `<WinnerPositionPanel>`, the winner page). One block per
 * outcome. Held rows come from the exact portfolio snapshot; fully exited
 * rows come from booked indexed position history. The market and winner
 * panels select canonical display rows and pass them into this surface.
 *
 * Current value and unrealised P&L come from the row's indexed `displayMark`;
 * spot price comes from indexed `currentPriceD9`. Chain reads remain isolated
 * to quick-trade execution preflight. Resolved states (won / lost / sold) come
 * from `resultState`. Every state shares one layout — the Unrealised header
 * cell + 2x2 stat grid — with quick-trade chips only while open and a drained
 * note on a loss (MOR-624). Prize-pool detail lives in the dedicated prize-pool
 * card below this one, not here.
 */

import { positionQuoteContext } from '@morfi/market-math';
import { useTranslations } from 'next-intl';
import { type ReactNode, useRef } from 'react';
import { OutcomeIdentityImage } from '@/components/market/identity/outcome-identity-image';
import {
  PositionQuickTrade,
  type PositionQuickTradeChip,
} from '@/components/market/position-quick-trade';
import { SharePnlButton } from '@/components/portfolio/share/share-pnl-button';
import { AnimatedButton } from '@/components/ui/animated-button';
import { AnimatedNumber } from '@/components/ui/animated-number';
import { CardSectionLabel } from '@/components/ui/card-section-label';
import { Icon } from '@/components/ui/icon';
import { Alert02Icon, TradeUpIconSolid } from '@/components/ui/icon/glyphs';
import { Panel } from '@/components/ui/panel';
import { PnlDirectionArrow } from '@/components/ui/pnl-direction-arrow';
import { ShrinkToFit, ShrinkToFitGroup } from '@/components/ui/shrink-to-fit';
import {
  type MarketOutcomeColorClasses,
  type OutcomeHueName,
  outcomeHueToStepVar,
  useMarketOutcomeColors,
} from '@/hooks/chart/use-outcome-color';
import type { PositionCardRow } from '@/hooks/positions/user-portfolio/display-types';
import { useTradingRestricted } from '@/hooks/trading/use-trading-restricted';
import { Link } from '@/i18n/navigation';
import { breakEvenMarketCapD6 } from '@/lib/chart/sdk-mapping';
import { type FormatMode, useFormattedCurrency, useQuickAmounts } from '@/lib/currency';
import { PNL_DUST_D6 } from '@/lib/market/position-pnl';
import {
  hasActiveSnapshotOutcomeBalance,
  hasPositiveSnapshotOutcomeBalance,
} from '@/lib/market/position-visibility';
import { useActiveNetwork } from '@/lib/network/use-active-network';
import { claimRightsMintAddress } from '@/lib/solana/claim-rights-mint';
import { computeChipBuyPlan, computeChipSellPlan } from '@/lib/trading/quick-trade-execution';
import { confettiOriginFromElement } from '@/lib/ui/confetti';
import { cn } from '@/lib/ui/utils';
import { useTradeExecutor } from '@/providers/trade-executor-provider';
import { type OnchainTradeData, useOnchainTradeData } from '@/queries/onchain-trade-data';
import { useTradeBalances } from '@/queries/trade-balances';
import { useChartSettingsStore } from '@/stores/chart-settings';

const ZERO: bigint = BigInt(0);
const MAX_SAFE_ANIMATED_D6: bigint = BigInt(Number.MAX_SAFE_INTEGER);
const POSITION_QUICK_BUY_TIER_INDEXES = [0, 1, 3, 4] as const;
const POSITION_QUICK_SELL_PERCENTAGES = [25, 50, 75, 100] as const;

function bi(v: string | undefined): bigint {
  // Backend money fields are integer D6/D9 strings; guard with a validity test
  // rather than try/catch so a malformed wire value falls back to 0 without
  // exception-as-control-flow (FT-12).
  if (v === undefined || !/^-?\d+$/.test(v)) return ZERO;
  return BigInt(v);
}

function unsignedWire(v: string | null): bigint | null {
  if (v === null || !/^(?:0|[1-9]\d*)$/.test(v)) return null;
  return BigInt(v);
}

/** A D6 value that ticks only while every intermediate integer is exactly representable. */
export function AnimatedD6({
  d6,
  signed = false,
  mode,
}: {
  d6: string;
  signed?: boolean;
  mode?: FormatMode;
}): React.ReactNode {
  const { formatD6, formatD6Signed } = useFormattedCurrency();
  const fmt = signed ? formatD6Signed : formatD6;
  const exactDisplay = fmt(d6, mode);
  const parsedD6 = /^-?(?:0|[1-9]\d*)$/.test(d6) ? BigInt(d6) : null;

  if (parsedD6 === null || parsedD6 < -MAX_SAFE_ANIMATED_D6 || parsedD6 > MAX_SAFE_ANIMATED_D6) {
    return <span className="tabular-nums">{exactDisplay}</span>;
  }

  return (
    <span className="tabular-nums">
      <span className="sr-only">{exactDisplay}</span>
      <span aria-hidden="true">
        <AnimatedNumber
          value={Number(parsedD6)}
          format={(intermediateD6: number): string =>
            fmt(BigInt(Math.round(intermediateD6)).toString(), mode)
          }
        />
      </span>
    </span>
  );
}

/** A D9 price that ticks only while every intermediate integer is exactly representable. */
export function AnimatedD9({ d9 }: { d9: string }): React.ReactNode {
  const { formatD9 } = useFormattedCurrency();
  const exactDisplay = formatD9(d9);
  const parsedD9 = /^(?:0|[1-9]\d*)$/.test(d9) ? BigInt(d9) : null;

  if (parsedD9 === null || parsedD9 > MAX_SAFE_ANIMATED_D6) {
    return <span className="tabular-nums">{exactDisplay}</span>;
  }

  return (
    <span className="tabular-nums">
      <span className="sr-only">{exactDisplay}</span>
      <span aria-hidden="true">
        <AnimatedNumber
          value={Number(parsedD9)}
          format={(intermediateD9: number): string =>
            formatD9(BigInt(Math.round(intermediateD9)).toString())
          }
        />
      </span>
    </span>
  );
}

/** Per-outcome market detail data supplied by `useGetMarket`.
 *  Current market cap comes from the backend; indexed supply anchors the
 *  graduated average-entry conversion. Image fields are App-DB fallbacks. */
export interface OutcomeMarketData {
  effectiveMarketCapD6: string;
  /** Indexed supply used by the graduated-token price→market-cap contract. */
  indexedOutcomeMintSupplyD6: string;
  /** Fallback image URL from the App-DB market-detail cache, used when the
   *  Timescale positions row has a null `outcomeImageUrl` (the Timescale
   *  `outcome.image_url` is NATS-owned and may lag the App-DB copy). */
  imageUrl?: string | null;
  imageNsfw?: boolean;
}

interface PositionCardProps {
  /** Exact display rows selected by the market or winner panel. */
  rows: readonly PositionCardRow[];
  /** Default market for blocks whose snapshot row is scoped by outcome mint. */
  market?: string;
  /** Tradeable-token route for the "Trade winner" CTA, keyed by outcomeMint. */
  winnerHref?: (row: PositionCardRow) => string;
  /** When true (chart y-axis = market cap), the current-price stat renders the
   *  backend-provided effective market cap. */
  marketCapMode?: boolean;
  /** Per-outcome effective market caps keyed by outcome index. */
  outcomeMarket?: Record<number, OutcomeMarketData>;
  /** When true (winner-detail page), won_holding positions use the 4-tile layout
   *  matching market-detail open positions, and buy chips render in success-green
   *  instead of outcome color. */
  winnerPage?: boolean;
  /** The market's full outcome count. When set, every block keys its on-chain
   *  trade query on the SAME count — sharing one cache entry with the page-level
   *  fetch instead of one overlapping-prefix entry per held outcome. */
  outcomeCount?: number;
  /** Exact pool snapshot already owned by the winner detail cache. */
  tradeData?: OnchainTradeData;
  className?: string;
}

export function PositionCard({
  rows,
  market,
  winnerHref,
  marketCapMode = false,
  outcomeMarket,
  winnerPage = false,
  outcomeCount,
  tradeData,
  className,
}: PositionCardProps): React.ReactNode | null {
  return (
    <PositionCardView
      rows={rows}
      {...(market !== undefined ? { market } : {})}
      marketCapMode={marketCapMode}
      {...(outcomeMarket !== undefined ? { outcomeMarket } : {})}
      {...(winnerHref !== undefined ? { winnerHref } : {})}
      winnerPage={winnerPage}
      {...(outcomeCount !== undefined ? { outcomeCount } : {})}
      {...(tradeData !== undefined ? { tradeData } : {})}
      {...(className !== undefined ? { className } : {})}
    />
  );
}

export interface PositionCardViewProps {
  /** Already-resolved position rows (one per held or fully exited outcome). */
  rows: readonly PositionCardRow[];
  /** Default market for blocks whose `row.market` is absent. */
  market?: string;
  marketCapMode?: boolean;
  outcomeMarket?: Record<number, OutcomeMarketData>;
  winnerHref?: (row: PositionCardRow) => string;
  winnerPage?: boolean;
  /** Full outcome count for the shared on-chain trade query (see `PositionCardProps`). */
  outcomeCount?: number;
  tradeData?: OnchainTradeData;
  className?: string;
}

/**
 * Presentational position card — the Panel + header + one `PositionBlock` per
 * outcome, for an already-selected display row set. The market and
 * winner panels own selection; the workspace showcase renders this directly
 * with canonical display fixtures to exercise every result state.
 */
export function PositionCardView({
  rows,
  market,
  marketCapMode = false,
  outcomeMarket,
  winnerHref,
  winnerPage = false,
  outcomeCount,
  tradeData,
  className,
}: PositionCardViewProps): React.ReactNode | null {
  const t = useTranslations('market.position');

  const ordered = sortDisplayPositionsActiveFirst(rows);
  const colorClasses = useMarketOutcomeColors(
    market ?? ordered[0]?.market,
    ordered.map((row) => ({ index: row.outcomeIndex })),
  );

  if (rows.length === 0) return null;

  // "Position" while any outcome is still held; "Position · resolved" once a
  // regular market resolves; "Position · closed" when the viewer fully exits a
  // live market or independently tradeable winner token (MOR-920, MOR-1459).
  const headState = resolveDisplayPositionHeadState(rows, { winnerPage });

  return (
    <Panel className={cn('flex flex-col gap-0 overflow-hidden p-0', className)}>
      <CardSectionLabel
        icon={TradeUpIconSolid}
        className="px-4 pt-3 pb-2.5"
        afterTitle={
          headState === 'closed' ? undefined : (
            <span aria-hidden className="relative inline-flex size-2">
              <span className="absolute inset-0 animate-ping rounded-full bg-feedback-success-accent opacity-60" />
              <span className="absolute inset-0 m-auto size-1.5 rounded-full bg-feedback-success-accent" />
            </span>
          )
        }
      >
        {headState === 'held'
          ? t('head')
          : headState === 'resolved'
            ? t('headResolved')
            : t('headClosed')}
      </CardSectionLabel>
      {ordered.map((row, i) => {
        const color = colorClasses[i];
        // Parallel arrays guarantee a match; the guard only satisfies TS.
        if (color === undefined) return null;
        // Only a zero-balance position collapses to the exited row. Every
        // positive balance needs lifecycle-aware valuation; a terminal loser
        // is deterministically worth zero and must not reuse its old curve.
        if (!hasPositiveSnapshotOutcomeBalance(row)) {
          return (
            <ExitedPositionRow
              key={row.positionId}
              row={row}
              color={color}
              outcomeData={outcomeMarket?.[row.outcomeIndex]}
              t={t}
            />
          );
        }
        return (
          <PositionBlock
            key={row.positionId}
            row={row}
            color={color}
            market={market ?? row.market}
            marketCapMode={marketCapMode}
            outcomeData={outcomeMarket?.[row.outcomeIndex]}
            winnerPage={winnerPage}
            {...(outcomeCount !== undefined ? { outcomeCount } : {})}
            {...(tradeData !== undefined ? { tradeData } : {})}
            {...(winnerHref !== undefined ? { winnerHref } : {})}
            t={t}
          />
        );
      })}
    </Panel>
  );
}

interface BlockProps {
  row: PositionCardRow;
  color: MarketOutcomeColorClasses;
  market: string;
  marketCapMode: boolean;
  outcomeData?: OutcomeMarketData | undefined;
  winnerHref?: (row: PositionCardRow) => string;
  winnerPage: boolean;
  outcomeCount?: number | undefined;
  tradeData?: OnchainTradeData | undefined;
  t: ReturnType<typeof useTranslations>;
}

function PositionBlock({
  row,
  color,
  market,
  marketCapMode,
  outcomeData,
  winnerHref,
  winnerPage,
  outcomeCount,
  tradeData,
  t,
}: BlockProps): React.ReactNode {
  // The trade executor publishes the one-tap trade's pending/result phase. Only
  // the matching outcome consumes it, so other position rows remain interactive.
  const autoPending = useChartSettingsStore((s) => s.autoTradePending[market] ?? null);
  const quickTradeProgress =
    autoPending !== null && autoPending.outcomeIndex === row.outcomeIndex ? autoPending : null;

  // The shared trade executor — null in the fixture-driven workspace showcase
  // (the card renders presentationally there, with no trade host).
  const executor = useTradeExecutor();
  const buyChipsBlocked = useBuyChipsBlocked(executor);
  const { toUsdc } = useFormattedCurrency();
  const network = useActiveNetwork();

  const hasHolding = hasPositiveSnapshotOutcomeBalance(row);
  const chipsActive = executor !== null && (hasActiveSnapshotOutcomeBalance(row) || winnerPage);
  const poolQuery = useOnchainTradeData(
    tradeData === undefined && chipsActive ? market : undefined,
    tradeData === undefined && chipsActive ? outcomeCount : undefined,
    tradeData === undefined && chipsActive
      ? [{ outcomeIndex: row.outcomeIndex, outcomeMint: row.outcomeMint }]
      : [],
  );
  const td = tradeData ?? poolQuery.data;
  const poolUnavailable = tradeData === undefined && (poolQuery.isError || poolQuery.isStale);
  const checkedPoolContext =
    td !== undefined
      ? positionQuoteContext(td, row.market, row.outcomeIndex, row.outcomeMint, poolUnavailable)
      : null;
  const poolContext = checkedPoolContext?.available === true ? checkedPoolContext.value : null;
  // Wallet balances are execution-only sell preflight. Bonding-curve exits
  // burn paired outcome + claim-right tokens; CP exits burn only the outcome
  // token. Display values never wait on this query.
  const claimRightsMint =
    poolContext?.poolModel === 'bonding_curve'
      ? claimRightsMintAddress(market, row.outcomeIndex, network)
      : undefined;
  const walletBalancesQuery = useTradeBalances(
    chipsActive ? row.wallet : undefined,
    chipsActive ? row.outcomeMint : undefined,
    chipsActive ? claimRightsMint : undefined,
  );
  const exactWalletBalances =
    walletBalancesQuery.isPlaceholderData === true ||
    walletBalancesQuery.isError ||
    walletBalancesQuery.isStale
      ? undefined
      : walletBalancesQuery.data;
  const onchainOutcomeBalanceD6 = rawBalanceD6(exactWalletBalances?.outcomeRawBalance);
  const onchainClaimRightsBalanceD6 =
    claimRightsMint === undefined ? null : rawBalanceD6(exactWalletBalances?.claimRightsRawBalance);
  const valueD6 = row.displayMark.currentValueD6;
  const unrealisedD6 = row.displayMark.unrealisedPnlD6;
  const realized = bi(row.realizedPnlD6);
  const isWon = row.resultState === 'won_holding' || row.resultState === 'won_sold';
  const isLost = row.resultState === 'lost';
  const avgEntryD9 = row.displayMark.averageEntryPriceD9;
  const spotPriceD9 = unsignedWire(row.currentPriceD9);

  // Every state — open, sold-out, won, lost — uses the same Unrealised header
  // cell + 4-tile 2×2 grid, so a resolved position reads consistently with a
  // held one (MOR-624). A truly zero-balance position has no live holdings, so
  // its unrealised PnL is pinned to $0 rather than a residual.
  const noLiveHoldings = !hasHolding;
  const headerUnreal = noLiveHoldings ? ZERO : unrealisedD6;

  // Anchors the quick-trade success confetti over this position block (MOR-625).
  const blockRef = useRef<HTMLDivElement>(null);

  const effectiveMarketCapD6 = outcomeData ? bi(outcomeData.effectiveMarketCapD6) : ZERO;
  const showMcap = marketCapMode && effectiveMarketCapD6 > ZERO;
  const averageEntryMarketCapD6 = averageMarketCapD6({
    averageEntryPriceD9: avgEntryD9,
    outcomeIndex: row.outcomeIndex,
    outcomeData,
    tradeData: td,
  });

  // The card chips are self-contained one-tap trades: they price the trade on
  // press via the shared pure estimate + `-N%` sizing helpers, then hand it to
  // the shared executor for the login/terms gate, signing, and submission —
  // WITHOUT touching the visible trade form. A captured origin (this block's
  // centre at tap time) rides along so the success confetti bursts over the
  // card, not the form (MOR-625). `buyChip` takes a LOCAL-currency amount and
  // converts via `toUsdc`, mirroring the form's amount field.
  function buyChip(localAmount: number): void {
    if (executor === null || poolContext === null) {
      return;
    }
    const plan = computeChipBuyPlan({
      buyUsdc: toUsdc(localAmount),
      context: poolContext,
    });
    if (plan === null) return;
    executor.executeBuy({
      marketAddress: market,
      outcomeIndex: row.outcomeIndex,
      outcomeMint: row.outcomeMint,
      amountInD6: plan.amountInD6,
      walletDebitD6: plan.walletDebitD6,
      feeExcludedQuoteInputD6: plan.feeExcludedQuoteInputD6,
      outcomeTokensOutD6: plan.outcomeTokensOutD6,
      desiredAmountD6: plan.desiredAmountD6,
      slippageBps: plan.slippageBps,
      suggestedSlippageBps: plan.suggestedSlippageBps,
      marketPhase: plan.marketPhase,
      confettiOrigin: confettiOriginFromElement(blockRef.current),
      chip: { outcomeIndex: row.outcomeIndex, direction: 'buy', buyUsdc: localAmount },
    });
  }

  function sellChip(pct: number): void {
    if (executor === null || poolContext === null) {
      return;
    }
    const plan = computeChipSellPlan({
      pct,
      indexedBalanceD6: bi(row.balanceD6),
      claimRightsBalanceD6: bi(row.claimRightsBalanceD6),
      onchainBalanceD6: onchainOutcomeBalanceD6,
      onchainClaimRightsBalanceD6,
      context: poolContext,
    });
    if (plan === null) return;
    executor.executeSell({
      marketAddress: market,
      marketTitle: row.marketTitle ?? undefined,
      outcomeIndex: row.outcomeIndex,
      outcomeMint: row.outcomeMint,
      outcomeLabel: row.outcomeLabel ?? `Outcome ${row.outcomeIndex}`,
      tokensNeededD6: plan.tokensNeededD6,
      expectedNetUsdcOutD6: plan.expectedNetUsdcOutD6,
      grossQuoteAmountD6: plan.grossQuoteAmountD6,
      feeExcludedQuoteOutputD6: plan.feeExcludedQuoteOutputD6,
      sellPenaltyFeeD6: plan.penaltyFeeD6,
      positionAccounting: {
        balanceD6: row.balanceD6,
        remainingCostBasisNetD6: row.remainingCostBasisNetD6,
        claimRightsBalanceD6: row.claimRightsBalanceD6,
        realizedPnlD6: row.realizedPnlD6,
        realizedCostBasisD6: row.realizedCostBasisD6,
      },
      slippageBps: plan.slippageBps,
      suggestedSlippageBps: plan.suggestedSlippageBps,
      marketPhase: plan.marketPhase,
      confettiOrigin: confettiOriginFromElement(blockRef.current),
      chip: { outcomeIndex: row.outcomeIndex, direction: 'sell', sellPercent: pct },
    });
  }

  // Curated per-currency one-tap buy tiers (local). `+$5/$10` etc. were raw USD.
  const buyTiers = useQuickAmounts({ max: 5 });
  const openBuyChips: readonly PositionQuickTradeChip[] = POSITION_QUICK_BUY_TIER_INDEXES.flatMap(
    (index) => {
      const amount = buyTiers[index];
      if (amount === undefined) return [];
      return [
        {
          kind: 'buy',
          label: `+${amount.label}`,
          onClick: (): void => buyChip(amount.value),
          id: { direction: 'buy', buyUsdc: amount.value },
          disabled: buyChipsBlocked,
        },
      ];
    },
  );
  const quickSellAvailable =
    poolContext !== null &&
    onchainOutcomeBalanceD6 !== null &&
    (poolContext.poolModel !== 'bonding_curve' || onchainClaimRightsBalanceD6 !== null);
  const openSellChips: readonly PositionQuickTradeChip[] = POSITION_QUICK_SELL_PERCENTAGES.map(
    (percentage) => ({
      kind: 'sell',
      label: `-${percentage}%`,
      onClick: (): void => sellChip(percentage),
      id: { direction: 'sell', sellPercent: percentage },
      disabled: !quickSellAvailable || executor?.isBuySellBlocked === true,
    }),
  );
  const openChips: readonly PositionQuickTradeChip[] = [...openBuyChips, ...openSellChips];

  return (
    <div
      ref={blockRef}
      className="border-t border-border-primary px-4 pt-3.5 pb-4 first:border-t-0"
    >
      <ShrinkToFitGroup className="grid grid-cols-[auto_minmax(0,1fr)_minmax(0,1fr)] items-start gap-3.5">
        <PositionThumb
          row={row}
          hue={color.hue}
          dim={isLost}
          showDot={!isLost}
          fallbackImageUrl={outcomeData?.imageUrl}
          fallbackImageNsfw={outcomeData?.imageNsfw}
        />
        <div className="min-w-0">
          <div
            className={cn(
              'truncate text-body leading-tight',
              isLost ? 'text-foreground-tertiary' : 'text-foreground-primary',
            )}
          >
            {isLost ? t('finalValue') : (row.outcomeLabel ?? t('currentValue'))}
          </div>
          <div data-testid="position-current-value" className="mt-0.5 min-w-0">
            <ShrinkToFit
              contentClassName={cn(
                'text-heading-lg font-bold tabular-nums',
                isWon && 'text-feedback-success-content',
              )}
            >
              <AnimatedD6 d6={(valueD6 ?? BigInt(0)).toString()} />
            </ShrinkToFit>
          </div>
        </div>
        <div className="min-w-0 text-right" data-testid="position-unrealised">
          <div className="truncate text-body text-foreground-tertiary leading-tight">
            {t('unrealised')}
          </div>
          <ShrinkToFit align="end" contentClassName="text-heading-lg font-bold tabular-nums">
            <PnlValue d6={headerUnreal ?? BigInt(0)} />
          </ShrinkToFit>
        </div>
      </ShrinkToFitGroup>

      <PositionMetrics
        investedD6={row.costBasisD6}
        marketCapMode={marketCapMode}
        currentMarketCapD6={showMcap ? effectiveMarketCapD6 : null}
        spotPriceD9={spotPriceD9}
        averageEntryD9={avgEntryD9}
        averageEntryMarketCapD6={averageEntryMarketCapD6}
        realizedD6={realized}
        penaltyD6={bi(row.realizedPenaltyD6)}
        t={t}
      />

      {isLost && (
        <div className="mt-3.5 flex min-h-10 items-center justify-center gap-1.5 rounded-control border border-feedback-error-border bg-feedback-error-surface px-3 py-2 text-caption font-medium text-feedback-error-content">
          <Icon icon={Alert02Icon} className="size-3.5 shrink-0" />
          <span className="text-center">{t('drainedNote')}</span>
        </div>
      )}

      {/* Quick-trade chips while the position has an active tradeable balance
          OR on the winner page. Positive legacy `sold_out` rows remain active
          because that state was derived from the removed linear dust mark.
          On the winner page the graduated winner is itself a live AMM token (`winnerPage` is
          only set by `WinnerPositionPanel`). Each chip executes self-contained
          through the shared trade executor, so the visible trade form never
          reacts. A plain resolved-market position (won, but no on-page AMM form)
          keeps its "Trade winner" CTA instead (MOR-624 / MOR-794). On market
          detail and winner detail use the same permanent behavior. */}
      {hasActiveSnapshotOutcomeBalance(row) || winnerPage ? (
        <PositionQuickTrade
          chips={openChips}
          color={color}
          progress={quickTradeProgress}
          greenBuyChips={winnerPage}
          className="mt-3"
        />
      ) : isWon && winnerHref !== undefined ? (
        <>
          <AnimatedButton variant="primary" className="mt-4 w-full" asChild>
            <Link href={winnerHref(row)}>{t('tradeWinner')}</Link>
          </AnimatedButton>
          <p className="mt-2 text-center text-caption text-foreground-tertiary">
            {t('winnerCaption')}
          </p>
        </>
      ) : null}
    </div>
  );
}

interface PositionMetricsProps {
  investedD6: string;
  marketCapMode: boolean;
  currentMarketCapD6: bigint | null;
  spotPriceD9: bigint | null;
  averageEntryD9: bigint | null;
  averageEntryMarketCapD6: bigint | null;
  realizedD6: bigint;
  /** Conviction penalty already booked into `realizedD6`; shown only when charged. */
  penaltyD6: bigint;
  t: ReturnType<typeof useTranslations>;
}

function PositionMetrics({
  investedD6,
  marketCapMode,
  currentMarketCapD6,
  spotPriceD9,
  averageEntryD9,
  averageEntryMarketCapD6,
  realizedD6,
  penaltyD6,
  t,
}: PositionMetricsProps): React.ReactNode {
  const feeT = useTranslations('trade.fees');
  const currentLabel = currentMarketCapD6 === null ? t('spotPrice') : t('marketCap');
  let currentValue: React.ReactNode = <AnimatedD9 d9="0" />;
  if (currentMarketCapD6 !== null) {
    currentValue = <AnimatedD6 d6={currentMarketCapD6.toString()} mode="compactDecimal" />;
  } else if (spotPriceD9 !== null) {
    currentValue = <AnimatedD9 d9={spotPriceD9.toString()} />;
  }
  const averageLabel = marketCapMode ? t('avgMarketCap') : t('avgEntry');
  const averageValue = marketCapMode ? (
    averageEntryMarketCapD6 === null ? (
      <AnimatedD6 d6="0" mode="compactDecimal" />
    ) : (
      <AnimatedD6 d6={averageEntryMarketCapD6.toString()} mode="compactDecimal" />
    )
  ) : averageEntryD9 === null ? (
    <AnimatedD9 d9="0" />
  ) : (
    <AnimatedD9 d9={averageEntryD9.toString()} />
  );

  return (
    <div className="mt-4 grid grid-cols-2 gap-2">
      <Stat
        testId="position-invested"
        label={t('invested')}
        value={<AnimatedD6 d6={investedD6} />}
      />
      <Stat testId="position-spot-price" label={currentLabel} value={currentValue} />
      <Stat testId="position-average-entry" label={averageLabel} value={averageValue} />
      <Stat
        testId="position-realised"
        label={t('realised')}
        value={<AnimatedD6 d6={realizedD6.toString()} signed />}
        tone={pnlTone(realizedD6)}
      />
      {penaltyD6 > ZERO && (
        <Stat
          testId="position-penalty"
          label={feeT('penalty_fee')}
          value={<AnimatedD6 d6={penaltyD6.toString()} />}
        />
      )}
    </div>
  );
}

function averageMarketCapD6({
  averageEntryPriceD9,
  outcomeIndex,
  outcomeData,
  tradeData,
}: {
  averageEntryPriceD9: bigint | null;
  outcomeIndex: number;
  outcomeData: OutcomeMarketData | undefined;
  tradeData: OnchainTradeData | undefined;
}): bigint | null {
  if (averageEntryPriceD9 === null || tradeData === undefined) return null;
  const pool = tradeData.outcomes.find((outcome) => outcome.outcomeIndex === outcomeIndex);
  if (pool === undefined) return null;
  return (
    breakEvenMarketCapD6(averageEntryPriceD9, {
      graduated: pool.cpSwap !== null,
      indexedOutcomeMintSupplyD6: outcomeData?.indexedOutcomeMintSupplyD6,
      initialOutcomeSupplyD6: pool.bondingCurve?.initialOutcomeSupplyD6,
      virtualQuoteD6: pool.bondingCurve?.virtualQuoteReserveD6,
    }) ?? null
  );
}

interface ExitedRowProps {
  row: PositionCardRow;
  color: MarketOutcomeColorClasses;
  outcomeData?: OutcomeMarketData | undefined;
  t: ReturnType<typeof useTranslations>;
}

// A fully-exited position (sold-out / won-then-sold / lost) rendered as one
// header-style row that mirrors a held position's "Current value" cell but keyed
// to the realised result (MOR-665): a "Realised win / loss" label over the
// realised PnL, drawn with the same up/down arrow as the Unrealised cell (via
// `PnlValue`) rather than a +/- sign, plus a Share affordance. Resolution
// losses are booked into realized PnL by the indexer and never use the legacy
// linear unrealized field.
function ExitedPositionRow({ row, color, outcomeData, t }: ExitedRowProps): React.ReactNode {
  const label = row.outcomeLabel ?? `Outcome ${row.outcomeIndex}`;
  const isLost = row.resultState === 'lost';
  const resultD6 = bi(row.realizedPnlD6);
  // Name the figure by polarity (same dust thresholds as `pnlTone`): a win, a
  // loss, or a neutral break-even.
  const realisedLabel =
    resultD6 > BigInt(PNL_DUST_D6)
      ? t('realisedWin')
      : resultD6 < -BigInt(PNL_DUST_D6)
        ? t('realisedLoss')
        : t('realised');

  return (
    <div className="flex items-center gap-3.5 border-t border-border-primary px-4 pt-3.5 pb-4 first:border-t-0">
      <PositionThumb
        row={row}
        hue={color.hue}
        dim={isLost}
        showDot={!isLost}
        fallbackImageUrl={outcomeData?.imageUrl}
        fallbackImageNsfw={outcomeData?.imageNsfw}
      />
      <div className="flex flex-1 flex-col justify-center self-stretch">
        <div className="text-body text-foreground-tertiary leading-tight">{realisedLabel}</div>
        <PnlValue d6={resultD6} className="text-heading" />
      </div>
      <SharePnlButton
        marketPubkey={row.market}
        marketTitle={row.marketTitle ?? row.market}
        walletPubkey={row.wallet}
        outcomeIndex={row.outcomeIndex}
        outcomeLabel={label}
        heroPnlD6={resultD6.toString()}
        closed
        showLabel
      />
    </div>
  );
}

function PositionThumb({
  row,
  hue,
  dim,
  showDot,
  sizeClass = 'size-[46px]',
  fallbackImageUrl,
  fallbackImageNsfw,
}: {
  row: PositionCardRow;
  hue: OutcomeHueName | undefined;
  dim: boolean;
  /** Held-state status dot (hue / winner) — off for exited rows. */
  showDot: boolean;
  /** Square size utility for the thumb; defaults to the full-block 46px. */
  sizeClass?: string;
  fallbackImageUrl?: string | null | undefined;
  fallbackImageNsfw?: boolean | undefined;
}): React.ReactNode {
  const isWinner = row.positionType === 'winner';
  // `row.outcomeImageUrl` comes from Timescale which may lag the App-DB copy
  // (Timescale `outcome.image_url` is NATS-owned, defaults to ''). Fall back
  // to the market-detail cache image so the thumbnail is never blank when the
  // App-DB has the URL but Timescale hasn't been synced yet.
  const imageUrl = row.outcomeImageUrl ?? fallbackImageUrl ?? null;
  const imageNsfw =
    row.outcomeImageUrl !== null ? row.outcomeImageNsfw : (fallbackImageNsfw ?? false);
  return (
    <div className={cn('relative shrink-0', sizeClass, dim && 'opacity-55')}>
      <OutcomeIdentityImage
        variant="thumbnail"
        entity={{
          kind: 'outcome',
          marketPubkey: row.market,
          outcomeIndex: row.outcomeIndex,
          hue,
          imageUrl,
          nsfw: imageNsfw,
        }}
        alt={row.outcomeLabel ?? ''}
        size={sizeClass}
        rounded="rounded-control"
        // ALLOW-NO-PREVIEW: outcome marker inside the position card; opening the lightbox would steal the card's tap target
        preview={false}
      />
      {showDot && (
        <span
          aria-hidden
          className={cn(
            'absolute -right-1 -bottom-1 size-[13px] rounded-full border-2 border-background-primary',
            isWinner
              ? 'bg-brand-400'
              : hue === undefined
                ? 'bg-background-tertiary'
                : 'bg-feedback-success-accent',
          )}
          style={
            isWinner || hue === undefined
              ? undefined
              : { backgroundColor: outcomeHueToStepVar(hue, 400) }
          }
        />
      )}
    </div>
  );
}

function PnlValue({ d6, className }: { d6: bigint; className?: string }): React.ReactNode {
  const up = d6 > BigInt(PNL_DUST_D6);
  const down = d6 < -BigInt(PNL_DUST_D6);
  const tone = up ? 'text-data-profit' : down ? 'text-data-loss' : 'text-foreground-disabled';
  // Direction is conveyed by the arrow orientation + tone; the value renders as
  // an unsigned magnitude, so no +/- sign. `className` lets a caller resize the
  // figure (e.g. the exited row runs a step smaller than the header cell).
  const magnitude = d6 < ZERO ? -d6 : d6;
  return (
    <div
      className={cn(
        'mt-0.5 inline-flex items-center gap-1.5 text-heading-lg font-bold tabular-nums transition-colors',
        tone,
        className,
      )}
    >
      {(up || down) && <PnlDirectionArrow valueD6={d6} className="size-4" />}
      <AnimatedD6 d6={magnitude.toString()} />
    </div>
  );
}

// Translucent stat tile — same card layout as the winner-page token stats
// (`winner-stat-tile.tsx`): label above value inside a `rounded-panel-row`
// `bg-background-tertiary/50` chip. `tone` colours the value for PnL cells.
function Stat({
  testId,
  label,
  value,
  tone,
}: {
  testId?: string;
  label: string;
  value: ReactNode;
  tone?: string;
}): React.ReactNode {
  return (
    <div
      className="flex flex-col gap-1 rounded-panel-row bg-background-tertiary/50 px-3 py-3"
      data-testid={testId}
    >
      <span className="text-caption text-foreground-tertiary">{label}</span>
      <span
        className={cn('text-body-lg font-medium tabular-nums', tone ?? 'text-foreground-primary')}
      >
        {value}
      </span>
    </div>
  );
}

function pnlTone(d6: bigint): string {
  if (d6 > BigInt(PNL_DUST_D6)) return 'text-data-profit';
  if (d6 < -BigInt(PNL_DUST_D6)) return 'text-data-loss';
  return 'text-foreground-disabled';
}

function sortDisplayPositionsActiveFirst(
  rows: readonly PositionCardRow[],
): readonly PositionCardRow[] {
  return rows.toSorted((left, right) => {
    const activeDelta =
      Number(hasActiveSnapshotOutcomeBalance(right)) -
      Number(hasActiveSnapshotOutcomeBalance(left));
    if (activeDelta !== 0) return activeDelta;
    return left.outcomeIndex - right.outcomeIndex;
  });
}

function resolveDisplayPositionHeadState(
  rows: readonly PositionCardRow[],
  { winnerPage }: { winnerPage: boolean },
): 'held' | 'resolved' | 'closed' {
  if (rows.some(hasActiveSnapshotOutcomeBalance)) return 'held';
  if (winnerPage) return 'closed';
  if (rows.some((row) => row.lifecycle === 'resolved' || row.lifecycle === 'graduated')) {
    return 'resolved';
  }
  return 'closed';
}

function rawBalanceD6(value: string | undefined): bigint | null {
  if (value === undefined || !/^\d+$/.test(value)) return null;
  return BigInt(value);
}

/** Buy chips are off while the executor blocks trades or the account is trading-restricted. */
function useBuyChipsBlocked(executor: ReturnType<typeof useTradeExecutor>): boolean {
  const tradingRestricted = useTradingRestricted();
  return tradingRestricted || executor?.isBuySellBlocked === true;
}
